import React from 'react';
import {
  getPaymentStatusMeta,
  resolveRosterDisplayStatus,
  formatPaymentAmount,
  PAYMENT_STATUS
} from '../../constants/paymentStatuses';
import './RosterPaymentCell.css';

export const PaidIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const FailIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const InsufficientFundsIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2v20" />
    <path d="M16 6.5c0-2-2-3-4-3s-4 1-4 2.5S10 8.5 12 9s4 1.5 4 3-2 3-4 3-4-1-4-2.5" />
  </svg>
);

export const PartialIcon = () => (
  <span className="rpc-partial-frac" aria-hidden>
    <span className="rpc-partial-frac__num">1</span>
    <span className="rpc-partial-frac__bar" />
    <span className="rpc-partial-frac__den">x</span>
  </span>
);

export const ROSTER_ACTION_LEGEND = [
  { id: 'paid', label: 'Paid', Icon: PaidIcon, className: 'rpc-action--paid' },
  { id: 'failure', label: 'Payment failure', Icon: FailIcon, className: 'rpc-action--fail' },
  { id: 'insufficient', label: 'Insufficient funds', Icon: InsufficientFundsIcon, className: 'rpc-action--insufficient' },
  { id: 'partial', label: 'Partial', Icon: PartialIcon, className: 'rpc-action--partial' }
];

export function RosterActionLegend({ className = '' }) {
  return (
    <div className={`rpc-legend${className ? ` ${className}` : ''}`} aria-label="Roster action key">
      <span className="rpc-legend-label">Actions</span>
      {ROSTER_ACTION_LEGEND.map(({ id, label, Icon, className: actionClass }) => (
        <span key={id} className="rpc-legend-item">
          <span className={`rpc-action rpc-action--legend ${actionClass}`} aria-hidden>
            <Icon />
          </span>
          {label}
        </span>
      ))}
      <span className="rpc-legend-item rpc-legend-item--note">Click status for waiver &amp; details</span>
    </div>
  );
}

function canQuickSetPaid(cell) {
  const status = String(cell?.status || '').toLowerCase();
  return status !== PAYMENT_STATUS.PAID && status !== PAYMENT_STATUS.NOT_APPLICABLE;
}

function canQuickSetFailure(cell) {
  const status = String(cell?.status || '').toLowerCase();
  return status !== PAYMENT_STATUS.PAID && status !== PAYMENT_STATUS.NOT_APPLICABLE;
}

function canQuickSetPartial(cell) {
  const status = String(cell?.status || '').toLowerCase();
  return status !== PAYMENT_STATUS.PAID && status !== PAYMENT_STATUS.NOT_APPLICABLE;
}

/**
 * Roster cell: status on top (opens editor); quick icons along the bottom.
 */
export default function RosterPaymentCell({ cell, busy, onOpen, onQuickSet }) {
  const displayStatus = resolveRosterDisplayStatus(cell.status, cell.required_by_date);
  const meta = getPaymentStatusMeta(displayStatus);
  const isAmount = cell.tracking_mode === 'amount' && cell.amount_due != null && Number(cell.amount_due) > 0;
  const progress =
    isAmount && cell.amount_due != null
      ? `${formatPaymentAmount(cell.net_paid || 0, cell.currency)} / ${formatPaymentAmount(cell.amount_due, cell.currency)}`
      : '';
  const dueHint = cell.required_by_date ? `Due ${cell.required_by_date}` : '';
  const titleParts = [meta.label, progress, dueHint].filter(Boolean);

  const paidEnabled = canQuickSetPaid(cell) && !busy;
  const failEnabled = canQuickSetFailure(cell) && !busy;
  const partialEnabled = canQuickSetPartial(cell) && !busy;

  const handleQuick = (status, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    onQuickSet(status);
  };

  return (
    <div className="rpc-cell">
      <button
        type="button"
        className="rpc-status"
        onClick={onOpen}
        style={{
          background: meta.bg,
          borderColor: meta.border,
          color: meta.color
        }}
        title={`${titleParts.join(' · ')} — open transactions`}
      >
        <span className="rpc-status-label">{busy ? '…' : meta.label}</span>
        {progress && <span className="rpc-status-meta">{progress}</span>}
      </button>

      <div className="rpc-actions" role="group" aria-label="Quick actions">
        <button
          type="button"
          className="rpc-action rpc-action--paid"
          disabled={!paidEnabled}
          onClick={(e) => handleQuick(PAYMENT_STATUS.PAID, e)}
          title="Mark paid"
          aria-label="Mark paid"
        >
          <PaidIcon />
        </button>
        <button
          type="button"
          className="rpc-action rpc-action--fail"
          disabled={!failEnabled}
          onClick={(e) => handleQuick(PAYMENT_STATUS.PAYMENT_FAILURE, e)}
          title="Mark payment failed"
          aria-label="Mark payment failed"
        >
          <FailIcon />
        </button>
        <button
          type="button"
          className="rpc-action rpc-action--insufficient"
          disabled={!failEnabled}
          onClick={(e) => handleQuick(PAYMENT_STATUS.INSUFFICIENT_FUNDS, e)}
          title="Mark insufficient funds"
          aria-label="Mark insufficient funds"
        >
          <InsufficientFundsIcon />
        </button>
        <button
          type="button"
          className="rpc-action rpc-action--partial"
          disabled={!partialEnabled}
          onClick={(e) => handleQuick(PAYMENT_STATUS.PARTIAL, e)}
          title="Mark partial payment"
          aria-label="Mark partial payment"
        >
          <PartialIcon />
        </button>
      </div>
    </div>
  );
}
